import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import styles from './Tooltip.module.css';

interface TooltipProps {
  content: string;
  children: React.ReactElement<any>;
  position?: 'top' | 'bottom' | 'left' | 'right';
  delay?: number;
  arrowAlign?: 'start' | 'center' | 'end';
  offset?: { x?: number; y?: number };
}

const Tooltip: React.FC<TooltipProps> = ({ 
  content, 
  children, 
  position = 'bottom',
  delay = 1000,
  arrowAlign = 'center',
  offset = { x: 0, y: 0 }
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const [actualArrowAlign, setActualArrowAlign] = useState(arrowAlign);
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const targetRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const showTooltip = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setIsVisible(true);
    }, delay);
  };

  const hideTooltip = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsVisible(false);
  };

  useEffect(() => {
    if (isVisible && targetRef.current && tooltipRef.current) {
      // Get the actual button element, not the wrapper
      const targetElement = targetRef.current.firstElementChild || targetRef.current;
      const targetRect = targetElement.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      
      let top = 0;
      let left = 0;

      const gap = 18; // Increased gap for better visual separation
      
      switch (position) {
        case 'top':
          top = targetRect.top - tooltipRect.height - gap + (offset.y || 0);
          left = targetRect.left + (targetRect.width - tooltipRect.width) / 2 + (offset.x || 0);
          break;
        case 'bottom':
          top = targetRect.bottom + gap + (offset.y || 0);
          left = targetRect.left + (targetRect.width - tooltipRect.width) / 2 + (offset.x || 0);
          break;
        case 'left':
          top = targetRect.top + (targetRect.height - tooltipRect.height) / 2 + (offset.y || 0);
          left = targetRect.left - tooltipRect.width - gap + (offset.x || 0);
          break;
        case 'right':
          top = targetRect.top + (targetRect.height - tooltipRect.height) / 2 + (offset.y || 0);
          left = targetRect.right + gap + (offset.x || 0);
          break;
      }

      // Store original left for arrow position calculation
      const originalLeft = left;
      
      // Ensure tooltip stays within viewport
      const padding = 8;
      let wasConstrained = false;
      
      if (left < padding) {
        left = padding;
        wasConstrained = true;
      }
      if (left + tooltipRect.width > window.innerWidth - padding) {
        left = window.innerWidth - tooltipRect.width - padding;
        wasConstrained = true;
      }
      if (top < padding) top = padding;
      if (top + tooltipRect.height > window.innerHeight - padding) {
        top = window.innerHeight - tooltipRect.height - padding;
      }

      // Calculate actual arrow alignment based on tooltip shift
      if (position === 'bottom' || position === 'top') {
        if (wasConstrained && arrowAlign === 'center') {
          // If constrained by viewport, calculate where arrow should point
          const targetCenterX = targetRect.left + targetRect.width / 2;
          const tooltipStart = left;
          const tooltipEnd = left + tooltipRect.width;
          
          // Calculate relative position of target center within tooltip
          const relativePos = (targetCenterX - tooltipStart) / tooltipRect.width;
          
          if (relativePos < 0.3) {
            setActualArrowAlign('start');
          } else if (relativePos > 0.7) {
            setActualArrowAlign('end');
          } else {
            setActualArrowAlign('center');
          }
        } else {
          setActualArrowAlign(arrowAlign);
        }
      } else {
        setActualArrowAlign(arrowAlign);
      }

      setCoords({ top, left });
    }
  }, [isVisible, position, arrowAlign, offset]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const handleMouseEnter = (e: React.MouseEvent) => {
    showTooltip();
    if (children.props.onMouseEnter) {
      children.props.onMouseEnter(e);
    }
  };

  const handleMouseLeave = (e: React.MouseEvent) => {
    hideTooltip();
    if (children.props.onMouseLeave) {
      children.props.onMouseLeave(e);
    }
  };

  const handleFocus = (e: React.FocusEvent) => {
    showTooltip();
    if (children.props.onFocus) {
      children.props.onFocus(e);
    }
  };

  const handleBlur = (e: React.FocusEvent) => {
    hideTooltip();
    if (children.props.onBlur) {
      children.props.onBlur(e);
    }
  };

  const childrenWithProps = React.cloneElement(children, {
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    onFocus: handleFocus,
    onBlur: handleBlur,
  });

  return (
    <>
      <div ref={targetRef} className={styles.tooltipWrapper}>
        {childrenWithProps}
      </div>
      {isVisible && ReactDOM.createPortal(
        <div 
          ref={tooltipRef}
          className={`${styles.tooltip} ${styles[position]} ${styles[`arrow-${actualArrowAlign}`]}`}
          style={{ top: `${coords.top}px`, left: `${coords.left}px` }}
          role="tooltip"
        >
          <div className={styles.tooltipContent}>
            {content}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default Tooltip;